import { useEffect, useState } from 'react';
import { useGame } from '../context/GameContext';
import { getAppUpdateUrl } from '../services/appVersion';
import { clearWorldsCache, getWorldId, loadWorldsManifest } from '../services/worldLoader';
import type { WorldsManifest } from '../types/world';
import type { CatalogWorld } from '../utils/worldCategories';

export function useWorldCatalog() {
  const { loadWorldData } = useGame();
  const [manifest, setManifest] = useState<WorldsManifest | null>(null);
  const [worlds, setWorlds] = useState<CatalogWorld[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<'loadError' | 'partialError' | null>(null);
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      setLoading(true);
      setError(null);
      try {
        // Retain the existing safe application-update check on catalogue navigation.
        if (import.meta.env.PROD) {
          const updateUrl = await getAppUpdateUrl(__APP_VERSION__, window.location.href);
          if (cancelled) return;
          if (updateUrl) { window.location.replace(updateUrl); return; }
        }
        const catalog = await loadWorldsManifest();
        if (cancelled) return;
        clearWorldsCache();
        const results = await Promise.all(catalog.worlds.filter(info => info.enabled).map(async info => ({
          info, world: await loadWorldData(getWorldId(info), catalog),
        })));
        if (cancelled) return;
        setManifest(catalog);
        setWorlds(results.filter((item): item is CatalogWorld => item.world !== null));
        if (results.some(item => item.world === null)) setError('partialError');
      } catch (error) {
        console.error('Failed to load world catalogue:', error);
        if (!cancelled) { setManifest(null); setWorlds([]); setError('loadError'); }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    void load();
    return () => { cancelled = true; };
  }, [loadWorldData, attempt]);

  return { manifest, worlds, loading, error, retry: () => setAttempt(value => value + 1) };
}
